"use client";

import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ExternalLink } from "lucide-react";
import { useTheme } from "../ThemeContext";
import SEOHead from "../SEOHead";
import { CASE_STUDIES } from "./caseStudyData";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate  = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const study = CASE_STUDIES[slug];

  const pageBg      = isDark ? "bg-black text-white"  : "bg-[#f0f4f0] text-gray-900";
  const greenH      = isDark ? "text-green-400"        : "text-green-700";
  const headingText = isDark ? "text-white"            : "text-gray-900";
  const bodyText    = isDark ? "text-gray-400"         : "text-gray-600";
  const cardBg      = isDark ? "bg-[#0f1a0f]"         : "bg-[#1a2e1a]";
  const borderGreen = "border border-green-400/30";

  // ── 404 ──────────────────────────────────────────────────────────────────
  if (!study) {
    return (
      <main className={`min-h-screen flex flex-col items-center justify-center px-6 pt-28 ${pageBg}`}>
        <h1 className="text-4xl font-bold text-green-400 mb-4">Case Study Not Found</h1>
        <p className={`${bodyText} mb-8`}>We couldn't find a case study for this project.</p>
        <button onClick={() => navigate("/portfolio")} className="bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-6 rounded-lg transition">
          Back to Portfolio
        </button>
      </main>
    );
  }

  const isFigma = study.category === "Figma Design";

  // ── All other projects except the current one ─────────────────────────────
  const allOtherProjects = Object.entries(CASE_STUDIES)
    .filter(([key]) => key !== slug)
    .map(([key, val]) => ({ slug: key, title: val.title, category: val.category }));

  return (
    <>
      <SEOHead
        title={`${study.title} — Case Study | Fiieve`}
        description={`Case study: ${study.title}. ${study.challenge.slice(0, 120)}...`}
        canonical={`https://fiieve.com/portfolio/${slug}`}
        ogImage={`https://fiieve.com${study.image}`}
      />

      <main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${pageBg}`}
        style={isDark ? darkGridStyle : lightGridStyle}
      >
        {!isDark && (
          <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"-10%", left:"-5%",  width:"50vw", height:"50vw", background:"radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)", filter:"blur(40px)" }} />
            <div style={{ position:"absolute", top:"-8%",  right:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",  filter:"blur(40px)" }} />
          </div>
        )}

        {/* ── HERO BANNER — matches OurWorkSection exactly ── */}
        <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
          <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
              {study.title}
            </h1>
            <nav aria-label="Breadcrumb" className="mt-3">
              <ol className="flex flex-wrap items-center justify-center gap-1 text-sm text-white" role="list">
                <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                <li aria-hidden="true" className="text-gray-400">/</li>
                <li><a href="/portfolio" className="hover:text-green-400 transition-colors">Portfolio</a></li>
                <li aria-hidden="true" className="text-gray-400">/</li>
                <li aria-current="page" className="text-green-400">{study.title}</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* ── PAGE CONTENT ── */}
        <div className="relative max-w-5xl mx-auto px-4 md:px-8 pb-20 pt-10" style={{ zIndex: 1 }}>

          {/* ── Category badge centered (no back button, no view site button here) ── */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex justify-center mb-6">
            <span className="inline-block bg-green-700/20 border border-green-700/50 text-green-700 text-sm font-extrabold uppercase tracking-widest px-5 py-2 rounded-full">
              {study.category}
            </span>
          </motion.div>

          {/* ── Project title — large, centered ── */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="text-center mb-8">
            <h2 className={`text-3xl md:text-5xl font-extrabold ${headingText} leading-tight`}>
              {study.title}
            </h2>
            <p className={`${bodyText} mt-3 text-base md:text-lg`}>
              {study.overview.industry} · {study.overview.projectType}
            </p>
          </motion.div>

          {/* ── Project IMAGE — reduced height ── */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="mb-6">
            <div className={`rounded-2xl overflow-hidden border ${borderGreen} w-full`}>
              <img
                src={study.image}
                alt={`${study.title} — project screenshot`}
                className="w-full h-auto object-contain"
                loading="eager"
              />
            </div>
          </motion.div>

          {/* ── View Site / Open Figma button — below image ── */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex justify-center mb-14">
            <a
              href={study.liveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold py-2.5 px-8 rounded-full transition-all duration-300 text-sm"
            >
              {isFigma ? "Open in Figma" : "View Live Site"}
              <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          </motion.div>

          {/* ── 1. PROJECT OVERVIEW ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="overview-heading" className="mb-14">
            <h2 id="overview-heading" className={`text-2xl font-bold ${greenH} mb-6 flex items-center gap-2`}>
              <span className="text-green-400">01.</span> Project Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(study.overview).map(([key, value]) => (
                <div key={key} className={`${cardBg} ${borderGreen} rounded-xl p-4`}>
                  <p className="text-gray-400 text-xs uppercase tracking-widest mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                  <p className="text-white font-semibold text-sm">{value}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* ── 2. THE CHALLENGE ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="challenge-heading" className="mb-14">
            <h2 id="challenge-heading" className={`text-2xl font-bold ${greenH} mb-4 flex items-center gap-2`}>
              <span className="text-green-400">02.</span> The Challenge
            </h2>
            <div className={`${cardBg} ${borderGreen} rounded-2xl p-6 md:p-8`}>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed">{study.challenge}</p>
            </div>
          </motion.section>

          {/* ── 3. OUR SOLUTION ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="solution-heading" className="mb-14">
            <h2 id="solution-heading" className={`text-2xl font-bold ${greenH} mb-4 flex items-center gap-2`}>
              <span className="text-green-400">03.</span> Our Solution
            </h2>
            <div className={`${cardBg} ${borderGreen} rounded-2xl p-6 md:p-8`}>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed">{study.solution}</p>
            </div>
          </motion.section>

          {/* ── 4. TECH STACK — green bg cards ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="tech-heading" className="mb-14">
            <h2 id="tech-heading" className={`text-2xl font-bold ${greenH} mb-6 flex items-center gap-2`}>
              <span className="text-green-400">04.</span> Technical Stack
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(study.techStack).map(([category, tools]) => (
                <div
                  key={category}
                  className={`${cardBg} border border-green-400/30 rounded-xl p-5 hover:border-green-400/60 transition-colors duration-300`}
                >
                  <p className="text-green-400 font-bold text-sm mb-3 uppercase tracking-wide">{category}</p>
                  <ul className="flex flex-col gap-1.5">
                    {tools.map((tool) => (
                      <li key={tool} className="flex items-center gap-2 text-gray-300 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                        {tool}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.section>

          {/* ── 5. RESULTS & IMPACT ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="results-heading" className="mb-14">
            <h2 id="results-heading" className={`text-2xl font-bold ${greenH} mb-6 flex items-center gap-2`}>
              <span className="text-green-400">05.</span> Results & Impact
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {study.results.map((result, i) => (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className={`${cardBg} ${borderGreen} rounded-xl p-5 flex items-start gap-3`}>
                  <span className="w-6 h-6 rounded-full bg-green-400/20 border border-green-400/40 text-green-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">✓</span>
                  <p className="text-white text-sm font-medium leading-relaxed">{result}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ── 6. TESTIMONIAL ── */}
          {study.testimonial && (
            <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="testimonial-heading" className="mb-14">
              <h2 id="testimonial-heading" className={`text-2xl font-bold ${greenH} mb-6 flex items-center gap-2`}>
                <span className="text-green-400">06.</span> Client Testimonial
              </h2>
              <blockquote className={`${cardBg} border-l-4 border-green-400 rounded-2xl p-6 md:p-8`}>
                <p className="text-white text-base md:text-lg leading-relaxed italic mb-4">"{study.testimonial.quote}"</p>
                <footer className="text-green-400 font-semibold text-sm">— {study.testimonial.name}</footer>
              </blockquote>
            </motion.section>
          )}

          {/* ── 7. ALL OTHER PROJECTS ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-labelledby="related-heading" className="mb-14">
            <h2 id="related-heading" className={`text-2xl font-bold ${greenH} mb-6 flex items-center gap-2`}>
              <span className="text-green-400">{study.testimonial ? "07." : "06."}</span> More Projects
            </h2>
            <div className="flex flex-col gap-3">
              {allOtherProjects.map((related, i) => (
                <motion.div
                  key={related.slug}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  className={`${cardBg} ${borderGreen} rounded-xl px-5 py-4 flex items-center justify-between gap-4 hover:border-green-400/60 transition-all duration-300 group`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="text-green-400 text-sm font-bold flex-shrink-0 w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm md:text-base truncate group-hover:text-green-400 transition-colors">
                        {related.title}
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5">{related.category}</p>
                    </div>
                  </div>
                  <Link
                    to={`/portfolio/${related.slug}`}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 border border-green-400/50 text-green-400 hover:bg-green-400 hover:text-black font-semibold text-xs px-4 py-2 rounded-full transition-all duration-300"
                  >
                    Case Study <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ── CTA ── */}
          <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} aria-label="Start a similar project">
            <div className={`${isDark ? "bg-black" : "bg-[#1a2e1a]"} border-2 border-green-400 rounded-2xl p-10 flex flex-col items-center gap-6 text-center`}>
              <div className="max-w-2xl">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Have a Similar Project?</h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  Whether it's a website, a Figma design, a mobile app, or a complex engineering system — we'd love to hear about your idea and help you bring it to life.
                </p>
              </div>
              <button
                onClick={() => navigate("/contact-us")}
                aria-label="Contact Fiieve to discuss your project"
                className="bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-8 rounded-lg transition-all duration-300"
              >
                Let's Talk
              </button>
            </div>
          </motion.section>

        </div>
      </main>
    </>
  );
}